import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Stack
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useSchool } from '../contexts/SchoolContext';
import Spinner from '../components/Spinner';

const statusOptions = [
  { value: 'open', label: 'Open' },
  { value: 'pending', label: 'Pending' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const SchoolParentTickets: React.FC = () => {
  const navigate = useNavigate();
  const { school, loading } = useSchool();
  const [tickets, setTickets] = useState<any[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<any>(null);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [responseStatus, setResponseStatus] = useState<'open' | 'pending' | 'resolved' | 'closed'>('pending');

  const loadTickets = useCallback(async () => {
    if (!school?._id) return;
    setLoadingTickets(true);
    setError(null);
    const authHeaders = {
      headers: {
        'x-auth-token': localStorage.getItem('school_token') || ''
      }
    };
    try {
      const response = await api.get(`/parent-tickets/school/${school._id}`, authHeaders);
      setTickets(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load parent tickets');
    } finally {
      setLoadingTickets(false);
    }
  }, [school?._id]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleSelectTicket = (ticket: any) => {
    setSelectedTicket(ticket);
    setResponseMessage('');
    setResponseStatus(ticket.status || 'pending');
    setSubmitError(null);
    setSubmitSuccess(null);
  };

  const handleSendResponse = async () => {
    if (!selectedTicket) return;
    if (!responseMessage.trim()) {
      setSubmitError('Response message is required.');
      return;
    }

    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const authHeaders = {
        headers: {
          'x-auth-token': localStorage.getItem('school_token') || ''
        }
      };
      const response = await api.post(
        `/parent-tickets/${selectedTicket.ticketId}/respond`,
        {
          message: responseMessage.trim(),
          status: responseStatus,
        },
        authHeaders
      );

      setSelectedTicket(response.data);
      setSubmitSuccess('Response submitted successfully.');
      setResponseMessage('');
      await loadTickets();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || 'Unable to send response.');
    }
  };

  const handleCloseTicket = async () => {
    if (!selectedTicket) return;

    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const authHeaders = {
        headers: {
          'x-auth-token': localStorage.getItem('school_token') || ''
        }
      };
      const response = await api.post(
        `/parent-tickets/${selectedTicket.ticketId}/respond`,
        {
          message: responseMessage.trim() || 'Closing ticket.',
          status: 'closed'
        },
        authHeaders
      );

      setSelectedTicket(response.data);
      setResponseStatus('closed');
      setResponseMessage('');
      setSubmitSuccess('Ticket closed successfully.');
      await loadTickets();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || 'Unable to close ticket.');
    }
  };

  const sortedTickets = tickets.slice().sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={4}><Spinner /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Parent Support Tickets
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/school/dashboard')}>
          Back to Dashboard
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Tickets</Typography>
            {loadingTickets ? (
              <Box display="flex" justifyContent="center" py={4}><Spinner /></Box>
            ) : sortedTickets.length === 0 ? (
              <Alert severity="info">No parent tickets have been submitted yet.</Alert>
            ) : (
              <Stack spacing={2}>
                {sortedTickets.map((ticket) => (
                  <Card
                    key={ticket.ticketId}
                    variant="outlined"
                    sx={{ cursor: 'pointer', borderColor: selectedTicket?.ticketId === ticket.ticketId ? 'primary.main' : 'divider' }}
                    onClick={() => handleSelectTicket(ticket)}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{ticket.subject || 'Parent ticket'}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {ticket.ticketId} • {ticket.category || 'General'} • {new Date(ticket.createdAt).toLocaleDateString()}
                      </Typography>
                      <Chip label={ticket.status || 'open'} size="small" color={ticket.status === 'closed' ? 'default' : ticket.status === 'resolved' ? 'success' : 'warning'} />
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            {!selectedTicket ? (
              <Typography color="text.secondary">Select a ticket to view details, respond, or close it.</Typography>
            ) : (
              <Box>
                <Typography variant="h5" sx={{ mb: 2 }}>{selectedTicket.subject || 'Parent Support Ticket'}</Typography>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                  Ticket ID: {selectedTicket.ticketId}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {selectedTicket.category || 'General'} • {selectedTicket.parentName || 'Parent'} • {new Date(selectedTicket.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>{selectedTicket.message}</Typography>
                {selectedTicket.attachment?.originalName && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">Attachment</Typography>
                    <Typography variant="body2" color="text.secondary">{selectedTicket.attachment.originalName}</Typography>
                  </Box>
                )}
                <Typography variant="subtitle1" sx={{ mb: 1 }}>Responses</Typography>
                {selectedTicket.responses?.length ? (
                  <Stack spacing={2} sx={{ mb: 3 }}>
                    {selectedTicket.responses.map((response: any, index: number) => (
                      <Paper key={index} variant="outlined" sx={{ p: 2, backgroundColor: '#ffffff', color: '#000000 !important' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#000000 !important' }}>{response.authorName || response.from}</Typography>
                        <Typography variant="body2" sx={{ mb: 1, color: '#4f4f4f !important' }}>{new Date(response.createdAt).toLocaleDateString()}</Typography>
                        <Typography component="div" variant="body1" sx={{ whiteSpace: 'pre-line', color: '#000000 !important', fontWeight: 500 }}>
                          {response.message || 'No response text provided.'}
                        </Typography>
                      </Paper>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary" sx={{ mb: 3 }}>No responses yet.</Typography>
                )}

                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
                {submitSuccess && <Alert severity="success" sx={{ mb: 2 }}>{submitSuccess}</Alert>}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="parent-ticket-status-label">Ticket Status</InputLabel>
                      <Select
                        labelId="parent-ticket-status-label"
                        label="Ticket Status"
                        value={responseStatus}
                        onChange={(event) => setResponseStatus(event.target.value as any)}
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <TextField
                  label="Response"
                  multiline
                  minRows={4}
                  fullWidth
                  value={responseMessage}
                  onChange={(event) => setResponseMessage(event.target.value)}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button variant="contained" startIcon={<Send />} onClick={handleSendResponse}>
                    Send response
                  </Button>
                  <Button variant="outlined" color="error" onClick={handleCloseTicket}>
                    Close ticket
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SchoolParentTickets;
